import { Controller } from "@hotwired/stimulus"
import Logger from '../lib/logger'

/**
 * Emoji Picker Controller
 * 
 * A reusable Stimulus controller for emoji selection functionality.
 * Follows Rails 8 and Stimulus 3 best practices with data-driven configuration.
 * 
 * Usage:
 *   <div data-controller="emoji-picker"
 *        data-emoji-picker-input-outlet="#message-input">
 *     <button type="button" data-action="emoji-picker#toggle">
 *       <i class="fas fa-smile"></i>
 *     </button>
 *     <div class="d-none" data-emoji-picker-target="picker">
 *       <button data-action="emoji-picker#select" data-emoji="😀">😀</button>
 *     </div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["picker", "frequentList", "grid"]
  static outlets = ["input"]
  static values = {
    frequent: { type: Array, default: ["👍", "❤️", "😊", "🎉", "👏"] },
    storageKey: { type: String, default: "emoji_picker_frequent" },
    maxFrequent: { type: Number, default: 10 }
  }

  connect() {
    Logger.debug('EmojiPicker controller connected', {
      storageKey: this.storageKeyValue,
      frequent: this.frequentValue
    })
    this.clickOutsideHandler = this.handleClickOutside.bind(this)
    this.loadFrequentEmojis()
  }

  disconnect() {
    this.removeClickOutsideListener()
  }

  // Toggle picker visibility
  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    const isHidden = this.pickerTarget.classList.contains("d-none") || !this.pickerTarget.classList.contains("show")
    isHidden ? this.show() : this.hide()
  }

  show() {
    this.pickerTarget.classList.remove("d-none")
    this.pickerTarget.classList.add("show")
    this.adjustHeight()
    this.addClickOutsideListener()
  }

  // Calculate available vertical space and adjust the internal grid height accordingly
  adjustHeight() {
    // Small delay to ensure the modal has rendered its layout
    setTimeout(() => {
      if (!this.hasPickerTarget || !this.hasGridTarget) return

      // Reset any previous inline styles to measure natural height
      this.gridTarget.parentElement.style.maxHeight = ''

      const triggerRect = this.element.getBoundingClientRect()

      // Find the message header which is usually the element that clips us from the top
      const header = document.getElementById('message-header')

      // If we're in a modal, the top boundary is the bottom of the header. 
      // If no header exists, use the closest clipping container or viewport top.
      const clippingContainer = this.element.closest('.overflow-hidden')

      let topBoundary = 0
      if (header) {
        topBoundary = header.getBoundingClientRect().bottom
      } else if (clippingContainer) {
        topBoundary = Math.max(0, clippingContainer.getBoundingClientRect().top)
      }

      // Distance from the top boundary to the top of the trigger button
      const availableSpaceTop = triggerRect.top - topBoundary

      // Leave a minimal padding from the top boundary
      const safeMaxTotalHeight = Math.max(150, availableSpaceTop - 15)

      // Measure natural height of the picker
      const pickerRect = this.pickerTarget.getBoundingClientRect()

      // If the picker's natural height exceeds safe space, we shrink the scrollable grid
      if (pickerRect.height > safeMaxTotalHeight) {
        // Find how much pixels the non-grid header elements consume (search, categories, frequent)
        const gridRect = this.gridTarget.parentElement.getBoundingClientRect()
        const headerSpace = pickerRect.height - gridRect.height

        // Constrain the grid container to the remaining safe height
        const targetGridHeight = safeMaxTotalHeight - headerSpace

        // Remove existing grid height classes
        this.gridTarget.parentElement.className = this.gridTarget.parentElement.className.replace(/\bemoji-grid-h-\d+\b/g, '')

        // Apply minimum bound to prevent it from becoming impossibly small, and round down to nearest 10 for our CSS classes
        const finalHeight = Math.max(80, Math.floor(targetGridHeight / 10) * 10)

        // Ensure we don't exceed the top CSS class definition (250px)
        const cssClassHeight = Math.min(250, finalHeight)
        this.gridTarget.parentElement.classList.add(`emoji-grid-h-${cssClassHeight}`)
      } else {
        // If it fits, just reset to natural state (which we removed line 66, but we should make sure we're on the default class)
        this.gridTarget.parentElement.className = this.gridTarget.parentElement.className.replace(/\bemoji-grid-h-\d+\b/g, '')
        this.gridTarget.parentElement.classList.add('emoji-grid-h-250') // default max
      }
    }, 10)
  }

  hide() {
    this.pickerTarget.classList.add("d-none")
    this.pickerTarget.classList.remove("show")
    this.removeClickOutsideListener()
  }

  // Select an emoji and insert it
  select(event) {
    event.preventDefault()
    const emoji = event.currentTarget.dataset.emoji || event.currentTarget.textContent.trim()

    if (!emoji) return

    // Dispatch custom event for other controllers to listen
    this.dispatch("selected", { detail: { emoji } })

    // Insert into outlet input if connected
    this.insertEmojiIntoInput(emoji)

    // Update frequent emojis
    this.updateFrequentEmojis(emoji)

    // Close picker
    this.hide()
  }

  // Insert emoji at cursor position in connected input
  insertEmojiIntoInput(emoji) {
    if (!this.hasInputOutlet) return

    const input = this.inputOutlet.element || this.inputOutlet
    if (!input || !input.tagName) return

    const start = input.selectionStart || 0
    const end = input.selectionEnd || 0
    const value = input.value || ""

    input.value = value.slice(0, start) + emoji + value.slice(end)
    input.focus()

    const newPosition = start + emoji.length
    input.setSelectionRange(newPosition, newPosition)

    // Trigger input event for other controllers
    input.dispatchEvent(new Event("input", { bubbles: true }))
  }

  // Load frequent emojis from localStorage
  loadFrequentEmojis() {
    try {
      const stored = localStorage.getItem(this.storageKeyValue)
      if (stored) {
        this.frequentValue = JSON.parse(stored)
        this.renderFrequentEmojis()
      }
    } catch (error) {
      Logger.warn('Failed to load frequent emojis', error)
    }
  }

  // Update frequent emojis list
  updateFrequentEmojis(emoji) {
    const frequent = [emoji, ...this.frequentValue.filter(e => e !== emoji)]
      .slice(0, this.maxFrequentValue)

    this.frequentValue = frequent

    try {
      localStorage.setItem(this.storageKeyValue, JSON.stringify(frequent))
    } catch (error) {
      Logger.warn('Failed to save frequent emojis', error)
    }

    this.renderFrequentEmojis()
  }

  // Render frequent emojis in the frequent list
  renderFrequentEmojis() {
    if (!this.hasFrequentListTarget) return

    this.frequentListTarget.innerHTML = this.frequentValue
      .map(emoji => `
        <button type="button" 
                class="btn btn-sm btn-outline-secondary emoji-btn"
                data-action="emoji-picker#select"
                data-emoji="${emoji}">
          ${emoji}
        </button>
      `).join("")
  }

  // Handle click outside to close picker
  handleClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hide()
    }
  }

  addClickOutsideListener() {
    // Use setTimeout to avoid immediate trigger from the toggle click
    setTimeout(() => {
      document.addEventListener("click", this.clickOutsideHandler)
    }, 10)
  }

  removeClickOutsideListener() {
    document.removeEventListener("click", this.clickOutsideHandler)
  }

  // Switch emoji category (smileys, people, objects, symbols)
  switchCategory(event) {
    const category = event.currentTarget.dataset.category

    // Remove active class from all category buttons
    this.element.querySelectorAll("[data-category]").forEach(btn => {
      btn.classList.remove("active")
    })

    // Add active class to selected
    event.currentTarget.classList.add("active")

    // Dispatch event for category change (handled by view or parent controller)
    this.dispatch("categoryChanged", { detail: { category } })
  }
}